import { message } from "antd"
import axios, { AxiosHeaders, AxiosRequestHeaders } from "axios"
import { onHttpRequestError } from "./onHttpRequestError"

axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL

export const getAdminToken = () => {
  try {
    const token = localStorage.getItem("caocaoAccessToken")
    return token ? `Bearer ${token}` : null
  } catch (ex) {
    return null
  }
}

const handleTokenExpired = () => {
  try {
    localStorage.removeItem("caocaoAccessToken")
    localStorage.removeItem("user")
    localStorage.removeItem("permissions")

    message.error("Hết hạn đăng nhập, vui lòng đăng nhập lại")

    if (typeof window !== "undefined") {
      window.location.href = "/login"
    }
  } catch (error) {
    console.error("Error during logout:", error)
  }
}

const adminAxios = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
})

adminAxios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handleTokenExpired()
    }
    return Promise.reject(error)
  },
)

const withAdminAuth = (headers: AxiosHeaders = new AxiosHeaders()): AxiosHeaders => {
  const token = getAdminToken()
  if (token) {
    headers.set("Authorization", token)
  }
  return headers
}

const catchTransactionError = async (asyncFunc: any, apiName = "") => {
  try {
    const response = await asyncFunc
    return response ? response.data : null
  } catch (error: any) {
    if (error.response && error.response.status === 401) {
      return null
    }

    let response = {
      data: { data: null, error: null },
      message: error.message,
    }
    if (error.response) {
      // Ưu tiên message từ API error nếu có
      const apiError = error.response.data?.error
      const apiMessage = apiError?.message || error.message
      response = {
        ...error.response,
        message: apiMessage || error.message,
      }
    }
    // onHttpRequestError throws exception, so it will propagate automatically
    onHttpRequestError(response, apiName)
  }
}

interface ConfigsProps {
  headers?: AxiosRequestHeaders
  apiName?: string
  params?: object
  data?: object
}

export const getAdminRequest = async <T = any>(url: string, configs: ConfigsProps = {}): Promise<T> =>
  catchTransactionError(
    adminAxios.get(url, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const postAdminRequest = async (url: string, data: any = null, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.post(url, data, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const putAdminRequest = async (url: string, data: any, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.put(url, data, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const patchAdminRequest = async (url: string, data: any, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.patch(url, data, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export const deleteAdminRequest = async (url: string, configs: ConfigsProps = {}) =>
  catchTransactionError(
    adminAxios.delete(url, {
      headers: withAdminAuth(new AxiosHeaders(configs.headers)),
      ...configs,
    }),
    configs.apiName,
  )

export class AdminHttpClient {
  static get = getAdminRequest
  static post = postAdminRequest
  static put = putAdminRequest
  static patch = patchAdminRequest
  static delete = deleteAdminRequest
}
